import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import {
    getAuth,
    Auth,
    OAuthProvider,
    signInWithPopup,
    UserCredential,
    User,
} from 'firebase/auth';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { FireService } from './fire.service';
import { AnalyticsService } from './analytics.service';
import { UserClaims } from 'src/firebase/firestore/models/users-claims/user-claims';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private stateSub = new BehaviorSubject<AuthState | null | undefined>(
        undefined
    );
    readonly auth: Auth;

    constructor(
        fire: FireService,
        private router: Router,
        private analytics: AnalyticsService
    ) {
        this.auth = getAuth(fire.app);

        this.auth.onAuthStateChanged((user) => {
            // console.log({ msg: 'auth-serivce.onAuthStateChanged', user });

            if (user == null) {
                this.stateSub.next(null as unknown as AuthState);
                return;
            }

            user.getIdTokenResult().then((token) =>
                this.stateSub.next({
                    claims: token.claims as UserClaims,
                    user,
                })
            );
            this.analytics.setUser(user.email, { ProviderId: user.providerId });
        });
    }

    get isSignedIn() {
        return this.stateSub.value != null;
    }

    async singIn(): Promise<UserCredential> {
        const provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({
            // Optional "tenant" parameter in case you are using an Azure AD tenant.
            // eg. '8eaef023-2b34-4da1-9baa-8bc8c9d6a490' or 'contoso.onmicrosoft.com'
            // or "common" for tenant-independent tokens.
            // The default value is "common".
            tenant: '4767f847-2f46-47ce-b25f-fa9af421268a',
        });
        return await signInWithPopup(this.auth, provider);
    }

    async signOut() {
        await this.auth.signOut();
        await this.router.navigate(['/']);
    }

    listenState(): Observable<AuthState | null | undefined> {
        return this.stateSub.asObservable();
    }

    getUser(): Promise<AuthState | null | undefined> {
        if (this.stateSub.value !== undefined) {
            return Promise.resolve(this.stateSub.value);
        }

        return new Promise((res) => {
            this.stateSub.subscribe((v) => {
                if (v === undefined) {
                    return;
                }

                res(v);
            });
        });
    }

    /**
     * Observable indicating whether the user is signed in or not.
     */
    get signedIn$(): Observable<boolean> {
        return this.listenState().pipe(map((s) => s != null));
    }

    async canActivate(
        next: ActivatedRouteSnapshot,
        _: RouterStateSnapshot
    ): Promise<boolean> {
        // return true;
        const state = await this.getUser();
        const userClaims: UserClaims | undefined = state?.claims;
        // console.log({ msg: 'auth-service.canActivate', next, state });
        // console.log({ msg: JSON.stringify(userClaims) });
        if (state == null) {
            this.router.navigate(['login']);
            return false;
        }

        // if no requirements then allow
        // TODO as interface
        // guardData = next.data; // as GuardData
        const guardData = next.data as GuardData;
        // console.log({ guardData: typeof guardData });
        // console.log({ msg: JSON.stringify(guardData) });

        // TODO
        if (guardData?.canView != null) {
            // console.log({ msg: 'auth-service.canActivate guardData?.canView != null' });

            if (await this.canViewProject(guardData.canView)) {
                return true;
            } else {
                // navigate to unauthorized if no valid roles present
                this.router.navigate(['unauthorized']);
                return false;
            }
        }
        return true;
    }

    async canViewProject(canViewFn: (_: UserClaims | undefined) => boolean) {
        // console.log({ msg: 'canViewProject' });
        const state = await this.getUser();
        const userClaims: UserClaims | undefined = state?.claims;
        if (userClaims != null) {
            return canViewFn(userClaims);
        }
        return false;
    }

    public static canViewLastArchon(userClaims: UserClaims | undefined) {
        return (
            AuthService.isDeveloper(userClaims) ||
            AuthService.isAllViewer(userClaims) ||
            !!userClaims?.projects?.includes('lastarchon')
        );
    }

    public static canViewBB(userClaims: UserClaims | undefined) {
        return (
            AuthService.isDeveloper(userClaims) ||
            AuthService.isAllViewer(userClaims) ||
            !!userClaims?.projects?.includes('bb')
        );
    }

    public static canViewCompany(userClaims: UserClaims | undefined) {
        return (
            AuthService.isDeveloper(userClaims) ||
            AuthService.isAllViewer(userClaims) ||
            !!userClaims?.projects?.includes('company')
        );
    }

    static isDeveloper(userClaims: UserClaims | undefined) {
        return !!userClaims?.developer;
    }

    static isAllViewer(userClaims: UserClaims | undefined) {
        return !!userClaims?.allviewer;
    }
}

export interface AuthState {
    user: User;
    claims: UserClaims;
}

export interface GuardData {
    canView?: (_: UserClaims | undefined) => boolean;
}
